import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Spin } from "antd";
import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";
import { HashRouter, Route, Routes } from "react-router-dom";
import { AdminRuntimeProvider } from "./AdminRuntime";
import { AppRouter } from "./AppRouter";
import { PlatformProviders } from "./PlatformProviders";
import { getCloudBaseWebClient } from "../lib/cloudBaseWebClient";
import { AdminLoginPage } from "../modules/auth/components/AdminLoginPage";
import { createCloudBaseAdminSession, type CloudBaseAdminSession } from "../modules/auth/data/cloudBaseAdminSession";
import { isPublicH5Hash } from "../modules/content-distribution/data/publicDocument";
import { PublicDocumentPage } from "../modules/content-distribution/pages/PublicDocumentPage";
import { PublicAnnouncementPage } from "../modules/content-distribution/pages/PublicAnnouncementPage";
import type { M1AppApi } from "../modules/m1-app/data/M1AppApi";
import { createCloudBaseM1AppApi } from "../modules/m1-app/data/cloudBaseM1AppApi";
import { createHttpM1AppApi } from "../modules/m1-app/data/httpAppApi";
import { AppStoreProvider } from "../modules/m1-app/store/AppStore";
import { UserStoreProvider } from "../modules/m2-user/store/UserStore";
import { createDevelopmentM2UserApi } from "../modules/m2-user/data/developmentUserApi";
import { createCloudBaseM2UserApi } from "../modules/m2-user/data/cloudBaseM2UserApi";

type CloudRuntimeConfig = {
  apiBaseUrl: string;
  dataMode: "http" | "cloudbase";
  envId: string;
  functionName: string;
  m2FunctionName?: string;
  postgresInstance: string;
  region: string;
  publishableKey: string;
};

type RuntimeConfig =
  | { mode: "development" }
  | { mode: "cloud"; cloud: CloudRuntimeConfig }
  | { mode: "error"; message: string };

export function resolveRuntimeConfig(env: Record<string, unknown>, isDevelopment: boolean): RuntimeConfig {
  const requestedMode = typeof env.VITE_ADMIN_RUNTIME_MODE === "string" ? env.VITE_ADMIN_RUNTIME_MODE.trim() : "";
  if (requestedMode && !["development", "ecs", "cloud"].includes(requestedMode)) {
    return { mode: "error", message: "VITE_ADMIN_RUNTIME_MODE 只能设置为 development 或 ecs。" };
  }
  const mode = requestedMode || (isDevelopment ? "development" : "cloud");
  if (mode === "development") return { mode: "development" };

  const requestedDataMode = typeof env.VITE_M1_DATA_MODE === "string" ? env.VITE_M1_DATA_MODE.trim() : "";
  if (requestedDataMode && !["http", "ecs", "cloudbase"].includes(requestedDataMode)) {
    return { mode: "error", message: "VITE_M1_DATA_MODE 只能设置为 http 或 ecs。" };
  }
  const dataMode: CloudRuntimeConfig["dataMode"] = requestedDataMode === "http" ? "http" : "cloudbase";
  const cloud = {
    apiBaseUrl: typeof env.VITE_ECS_API_BASE_URL === "string" && env.VITE_ECS_API_BASE_URL.trim()
      ? window.location.origin : typeof env.VITE_M1_API_BASE_URL === "string" && env.VITE_M1_API_BASE_URL.trim()
      ? env.VITE_M1_API_BASE_URL.trim().replace(/\/+$/, "") : window.location.origin,
    dataMode,
    envId: "ecs",
    functionName: typeof env.VITE_M1_ADMIN_FUNCTION_NAME === "string" && env.VITE_M1_ADMIN_FUNCTION_NAME.trim()
      ? env.VITE_M1_ADMIN_FUNCTION_NAME.trim()
      : "m1-app-admin",
    m2FunctionName: typeof env.VITE_M2_ADMIN_FUNCTION_NAME === "string" && env.VITE_M2_ADMIN_FUNCTION_NAME.trim()
      ? env.VITE_M2_ADMIN_FUNCTION_NAME.trim()
      : "m2-user-admin",
    postgresInstance: "yahshi",
    region: "ecs",
    publishableKey: "ecs-session",
  };
  return { mode: "cloud", cloud };
}

export function AppBootstrap() {
  const config = resolveRuntimeConfig(import.meta.env, import.meta.env.DEV);
  if (config.mode === "development") {
    return <AdminRuntimeProvider value={{ mode: "development" }}><AppStoreProvider><UserStoreProvider api={createDevelopmentM2UserApi()}><PlatformProviders><AppRouter /></PlatformProviders></UserStoreProvider></AppStoreProvider></AdminRuntimeProvider>;
  }
  if (config.mode === "error") {
    return (
      <main className="bootstrap-state">
        <Alert type="error" showIcon title="无法启动管理后台" description={config.message} />
      </main>
    );
  }
  if (isPublicH5Hash(window.location.hash)) {
    return (
      <AdminRuntimeProvider value={{ mode: "cloud" }}>
        <HashRouter>
          <Routes>
            <Route path="/public/docs/:appId/:type" element={<PublicDocumentPage />} />
            <Route path="/public/announcements/:appId" element={<PublicAnnouncementPage />} />
            <Route path="/public/announcements/:appId/:id" element={<PublicAnnouncementPage />} />
          </Routes>
        </HashRouter>
      </AdminRuntimeProvider>
    );
  }
  return <CloudAdminBootstrap config={config.cloud} />;
}

type CloudAdminBootstrapProps = PropsWithChildren<{
  config: CloudRuntimeConfig;
  sessionOverride?: CloudBaseAdminSession;
  apiOverride?: M1AppApi;
}>;

export function CloudAdminBootstrap({ config, sessionOverride, apiOverride, children }: CloudAdminBootstrapProps) {
  const queryClient = useQueryClient();
  const cloudClient = useMemo(() => getCloudBaseWebClient({
    envId: config.envId,
    region: config.region,
    publishableKey: config.publishableKey,
  }), [config.envId, config.publishableKey, config.region]);
  const session = useMemo(() => sessionOverride ?? createCloudBaseAdminSession({
    envId: config.envId,
    region: config.region,
    publishableKey: config.publishableKey,
  }, cloudClient), [cloudClient, config.envId, config.publishableKey, config.region, sessionOverride]);
  const [status, setStatus] = useState<"checking" | "signed-out" | "signed-in" | "unavailable">("checking");
  const [username, setUsername] = useState("");
  const [notice, setNotice] = useState("");
  const [checkVersion, setCheckVersion] = useState(0);
  const signingOutRef = useRef(false);

  const signOut = useCallback(async (reason = "") => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    setUsername("");
    setNotice(reason);
    setStatus("signed-out");
    queryClient.clear();
    try {
      await session.signOut();
    } catch {
      // The protected UI is already closed; a remote sign-out failure must not
      // leave stale admin data visible or create an unhandled rejection.
    } finally {
      signingOutRef.current = false;
    }
  }, [queryClient, session]);

  const api = useMemo(() => {
    if (apiOverride) return apiOverride;
    const onUnauthorized = () => signOut("登录状态已失效，请重新登录");
    const httpApi = createHttpM1AppApi({
      baseUrl: config.apiBaseUrl,
      getAccessToken: session.getAccessToken,
      onUnauthorized,
    });
    if (config.dataMode === "cloudbase") {
      const rpcApi = createCloudBaseM1AppApi({
        client: cloudClient,
        postgresInstance: config.postgresInstance,
        functionName: config.functionName,
        onUnauthorized,
      });
      // ECS RPC handles stored data; provider reconciliation runs in the HTTP service.
      return { ...rpcApi, syncPaymentOrder: httpApi.syncPaymentOrder, syncPaymentRefund: httpApi.syncPaymentRefund };
    }
    return httpApi;
  }, [apiOverride, cloudClient, config.apiBaseUrl, config.dataMode, config.functionName, config.postgresInstance, session, signOut]);
  const userApi = useMemo(() => createCloudBaseM2UserApi({
    client: cloudClient,
    ...(config.m2FunctionName ? { functionName: config.m2FunctionName } : {}),
    onUnauthorized: () => signOut("登录状态已失效，请重新登录"),
  }), [cloudClient, config.m2FunctionName, signOut]);

  useEffect(() => {
    let active = true;
    setStatus("checking");
    session.getCurrentUsername()
      .then((current) => {
        if (!active) return;
        setUsername(current ?? "");
        setStatus(current ? "signed-in" : "signed-out");
      })
      .catch(() => {
        if (active) setStatus("unavailable");
      });
    return () => { active = false; };
  }, [checkVersion, session]);

  if (status === "checking") {
    return <main className="bootstrap-state" aria-busy="true" aria-label="正在检查登录状态"><Spin size="large" /></main>;
  }
  if (status === "unavailable") {
    return (
      <main className="bootstrap-state">
        <Alert
          type="error"
          showIcon
          title="暂时无法验证登录状态"
          description="请检查网络连接后重试。"
          action={<Button icon={<RefreshCw size={16} aria-hidden="true" />} onClick={() => setCheckVersion((value) => value + 1)}>重试</Button>}
        />
      </main>
    );
  }
  if (status === "signed-out") {
    return (
      <AdminLoginPage
        notice={notice}
        onLogin={async (account, password) => {
          const current = await session.signIn(account, password);
          setNotice("");
          setUsername(current);
          setStatus("signed-in");
        }}
      />
    );
  }
  return (
    <AdminRuntimeProvider value={{
      mode: "cloud",
      username,
      signOut: () => signOut(),
      apiBaseUrl: config.apiBaseUrl || "https://api.yahshi.cn",
      getAccessToken: session.getAccessToken,
    }}>
      <AppStoreProvider api={api}><UserStoreProvider api={userApi}><PlatformProviders options={{
        baseUrl: config.apiBaseUrl || "https://api.yahshi.cn",
        getAccessToken: session.getAccessToken,
        onUnauthorized: () => signOut("登录状态已失效，请重新登录"),
      }}>{children ?? <AppRouter />}</PlatformProviders></UserStoreProvider></AppStoreProvider>
    </AdminRuntimeProvider>
  );
}
